// Import directly from the core implementation to avoid circular deps
import { defineStore } from '~/plugins/pinia-core'
import { defaultParams } from '@/utils/constants'
import { getDeserializedCategoryProducts } from '@/utils/deserialization'

// We'll need these to access Nuxt's $axios
let $axios = null

export const useSearchStore = defineStore('search', {
  state: () => ({
    results: 0,
    page: 0,
    products: [],
    autocompleteResults: [],
  }),

  getters: {
    // No getters in the original Vuex module, but we can add some if needed
    // For example: hasResults: (state) => state.results > 0
  },

  actions: {
    resetSearchResults() {
      this.results = 0
      this.page = 0
      this.products = []
    },

    addMoreSearchResults(results) {
      this.results = results.results
      this.products = [...this.products, ...results.products]
    },

    setSearchResultsPage(page) {
      this.page = page
    },

    setAutocompleteResults(results) {
      this.autocompleteResults = results
    },

    async getSearchResults(query) {
      this.resetSearchResults()
      await this.loadMoreResults(query)
    },

    async loadMoreResults(query, { rootStore = null } = {}) {
      query = query || ''
      query = query.replace(/ +/g, '+')

      // Handle global loading/error states
      if (rootStore) {
        rootStore.removeError('search')
        rootStore.addLoading('search')
      }

      const nextPage = this.page + 1

      // Get coupon code from root store if available
      const couponCode = rootStore?.couponCode
      const params = {
        page: nextPage,
        ...(couponCode && { couponCode }),
        ...defaultParams,
      }

      try {
        // Use the injected $axios instance
        const response = await $axios.get(`search/${query}`, { params })
        const { data: searchResults } = response.data
        const { products } = searchResults
        const deserializedProducts = getDeserializedCategoryProducts(products)
        const deserializedSearchResults = {
          ...searchResults,
          products: deserializedProducts,
        }

        this.setSearchResultsPage(nextPage)
        this.addMoreSearchResults(deserializedSearchResults)
      } catch (error) {
        if (error.response?.status === 401 && rootStore) {
          // Handle authentication error with silent logout
          await rootStore.silentLogOut({ source: 'search/loadMoreResults' })

          try {
            // Use the injected $axios instance
            const response = await $axios.get(`search/${query}`, { params })
            const { data: searchResults } = response.data
            const { products } = searchResults
            const deserializedProducts = getDeserializedCategoryProducts(products)
            const deserializedSearchResults = {
              ...searchResults,
              products: deserializedProducts,
            }

            this.setSearchResultsPage(nextPage)
            this.addMoreSearchResults(deserializedSearchResults)
          } catch (retryError) {
            if (rootStore) {
              rootStore.addError({ id: 'product', error: retryError })
            }
          }
        }

        if (rootStore) {
          rootStore.addError({ id: 'product', error })
        }
      } finally {
        if (rootStore) {
          rootStore.removeLoading('search')
        }
      }
    },

    async getAutocompleteResults(query, { rootStore = null } = {}) {
      if (rootStore) {
        rootStore.removeError('autocomplete')
        rootStore.addLoading('autocomplete')
      }

      try {
        // Use the injected $axios instance
        const params = { search: query, ...defaultParams }
        const response = await $axios.get('/search/autocomplete', { params })

        const { data: results } = response.data

        this.setAutocompleteResults(results)
      } catch (error) {
        if (rootStore) {
          rootStore.addError({ id: 'autocomplete', error })
        }
      } finally {
        if (rootStore) {
          rootStore.removeLoading('autocomplete')
        }
      }
    },
  },
})

// Initialization function to provide Nuxt's axios to the store
export function initializeSearchStore(nuxtAxios) {
  $axios = nuxtAxios
}
